import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Repeat, FilePlus2, BellOff, ExternalLink } from "lucide-react";
import { api, apiErr, money, fmtDate } from "./lib/api";
import { Card, Btn, PageHeader, Badge, Th, Td, Modal, Select, Label } from "./ui";

const MONTHS = (() => {
  const out = []; const d = new Date();
  for (let i = 0; i < 13; i++) {
    const y = d.getFullYear(), m = d.getMonth();
    const v = `${y}-${String(m + 1).padStart(2, "0")}`;
    out.push([v, `${d.toLocaleString("en-IN", { month: "long" })} ${y}${i === 0 ? " (current)" : ""}`]);
    d.setMonth(m + 1);
  }
  return out;
})();
const ORDER = ["Monthly", "Quarterly", "Bimonthly", "Annual", "Renewal"];

const CFG = {
  domestic: { title: "Recurring — Due for Billing", due: "/recurring/due", act: (id, a) => `/recurring/${id}/${a}`, view: (id) => `/invoices/${id}`, edit: (id) => `/invoices/${id}/edit`, cur: (i) => i.currency },
  export: { title: "Export Recurring — Due for Billing", due: "/export-invoices/recurring/due", act: (id, a) => `/export-invoices/${id}/recurring/${a}`, view: (id) => `/exports/${id}`, edit: (id) => `/exports/${id}/edit`, cur: (i) => i.currency },
};

export function ExportRecurring() { return <Recurring variant="export" />; }

export function Recurring({ variant = "domestic" }) {
  const cfg = CFG[variant];
  const nav = useNavigate();
  const [month, setMonth] = useState(MONTHS[0][0]);
  const [rtype, setRtype] = useState("");
  const [data, setData] = useState({ items: [], counts: {}, labels: {}, total: 0, overdue_count: 0 });
  const [busy, setBusy] = useState(null);
  const [dismiss, setDismiss] = useState(null);
  const load = () => api.get(cfg.due, { params: { month, ...(rtype ? { rtype } : {}) } }).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, rtype]);
  const monthLabel = month === "overdue" ? "earlier months (overdue)" : (MONTHS.find(([v]) => v === month) || [])[1];

  const make = async (inv) => {
    setBusy(inv.id);
    try { const { data: d } = await api.post(cfg.act(inv.id, "make")); toast.success(`Draft ${d.invoice_number} created`); nav(cfg.edit(d.id)); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  const doDismiss = async () => {
    try { await api.post(cfg.act(dismiss.id, "dismiss")); toast.success("Reminder dismissed"); setDismiss(null); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  let lastType = null;
  return (
    <div className="animate-fade-up">
      <PageHeader title={cfg.title} subtitle={`${data.total} bill${data.total !== 1 ? "s" : ""} due in ${monthLabel || "—"} · a bill leaves this list as soon as its invoice is made`}
        actions={<div className="flex items-end gap-3">
          <div><Label>Show bills due in</Label>
            <Select data-testid="rec-month-select" className="min-w-[240px]" value={month} onChange={(e) => { setMonth(e.target.value); setRtype(""); }}>
              {data.overdue_count > 0 && <option value="overdue">Overdue — earlier months ({data.overdue_count})</option>}
              {MONTHS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select></div>
        </div>} />
      {data.overdue_count > 0 && month !== "overdue" && <button data-testid="rec-overdue-banner" onClick={() => setMonth("overdue")} className="mb-3 w-full text-left px-4 py-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm hover:bg-rose-500/20">{data.overdue_count} recurring bill{data.overdue_count !== 1 ? "s" : ""} from earlier months still not made — click to view</button>}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="rec-type-chips">
        <button data-testid="rec-type-all" onClick={() => setRtype("")} className={`px-3 py-1.5 rounded-full text-xs border ${!rtype ? "bg-slate-100 text-slate-900 border-slate-100" : "text-slate-400 border-slate-700 hover:text-slate-200"}`}>All ({data.total})</button>
        {ORDER.map((t) => (
          <button key={t} data-testid={`rec-type-${t}`} onClick={() => setRtype(rtype === t ? "" : t)}
            className={`px-3 py-1.5 rounded-full text-xs border ${rtype === t ? "bg-slate-100 text-slate-900 border-slate-100" : "text-slate-400 border-slate-700 hover:text-slate-200"}`}>{data.labels[t] || t} ({data.counts[t] || 0})</button>
        ))}
      </div>
      <Card className="overflow-x-auto md:overflow-visible">
        <table className="w-full min-w-[900px]">
          <thead className="bg-slate-900/40"><tr><Th>Type</Th><Th>Last Invoice</Th><Th>Client</Th><Th>Billed On</Th><Th>Due On</Th><Th>Amount</Th><Th>Particulars</Th><Th></Th></tr></thead>
          <tbody>
            {data.items.map((i) => {
              const header = i.recurring_type !== lastType; lastType = i.recurring_type;
              return [
                header && !rtype && <tr key={`h-${i.recurring_type}`} className="bg-slate-900/60"><Td colSpan={8} className="text-xs font-mono uppercase tracking-wider text-sky-300 py-2">{data.labels[i.recurring_type] || i.recurring_type} · {data.counts[i.recurring_type]}</Td></tr>,
                <tr key={i.id} className="border-t border-slate-800/60 hover:bg-slate-800/30" data-testid={`recurring-row-${i.id}`}>
                  <Td><Badge tone="sky"><Repeat size={11} className="inline mr-1" />{i.recurring_type === "Bimonthly" ? "Half-yearly" : i.recurring_type}</Badge></Td>
                  <Td className="font-mono whitespace-nowrap"><button className="hover:text-sky-300 inline-flex items-center gap-1" onClick={() => nav(cfg.view(i.id))}>{i.invoice_number} <ExternalLink size={12} /></button></Td>
                  <Td className="text-slate-300 max-w-[220px] truncate">{i.client_name || "—"}</Td>
                  <Td className="text-slate-400 whitespace-nowrap">{fmtDate(i.date)}</Td>
                  <Td className="whitespace-nowrap"><span className={i.days_overdue > 0 ? "text-rose-400" : "text-slate-200"}>{fmtDate(i.next_due)}</span>
                    <div className="text-[10px] font-mono text-slate-500">{i.days_overdue > 0 ? `${i.days_overdue} days overdue` : i.days_overdue === 0 ? "today" : `in ${-i.days_overdue} days`}</div></Td>
                  <Td className="font-mono whitespace-nowrap">{money(i.total, cfg.cur(i))}</Td>
                  <Td className="text-slate-400 text-xs max-w-[170px] truncate" title={i.items_preview}>{(i.items_preview || i.notes || i.remarks || "").slice(0, 80)}</Td>
                  <Td className="whitespace-nowrap">
                    <Btn data-testid={`rec-make-${i.id}`} className="px-3 py-1.5 text-xs mr-2" disabled={busy === i.id} onClick={() => make(i)}><FilePlus2 size={14} /> Make Invoice</Btn>
                    <Btn data-testid={`rec-dismiss-${i.id}`} variant="ghost" className="px-3 py-1.5 text-xs" onClick={() => setDismiss(i)}><BellOff size={14} /> Dismiss</Btn>
                  </Td>
                </tr>];
            })}
            {data.items.length === 0 && <tr><Td colSpan={8} className="text-slate-500 py-8 text-center">Nothing due for billing in this window.</Td></tr>}
          </tbody>
        </table>
      </Card>
      <Modal open={Boolean(dismiss)} onClose={() => setDismiss(null)} title="Dismiss reminder" danger>
        <p className="text-slate-300 text-sm mb-4">Stop reminding about <b>{dismiss?.invoice_number}</b> ({dismiss?.client_name})? The invoice itself is untouched; it just leaves this list. You can re-open it from the invoice page.</p>
        <div className="flex justify-end gap-2"><Btn variant="ghost" onClick={() => setDismiss(null)}>Keep</Btn><Btn data-testid="rec-confirm-dismiss" variant="danger" onClick={doDismiss}><BellOff size={16} /> Dismiss</Btn></div>
      </Modal>
    </div>
  );
}
